export const formatCurrency = (amount: number | undefined | null): string => {
  return `₹${(amount ?? 0).toLocaleString('en-IN')}`;
};

/**
 * PyMongo returns naive datetime strings (no timezone suffix).
 * We treat all such strings as UTC and convert to IST (Asia/Kolkata).
 */
function toUTC(dateStr: string): Date {
  if (!dateStr) return new Date(NaN);
  const hasTimezone = dateStr.endsWith('Z') || /[+-]\d{2}:?\d{2}$/.test(dateStr);
  const hasTime = dateStr.includes('T') || dateStr.includes(' ');
  const normalized = hasTime && !hasTimezone ? dateStr.replace(' ', 'T') + 'Z' : dateStr;
  return new Date(normalized);
}

const IST: Intl.DateTimeFormatOptions = { timeZone: 'Asia/Kolkata' };

export const formatDate = (dateStr: string): string => {
  const date = toUTC(dateStr);
  return date.toLocaleDateString('en-IN', {
    ...IST,
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  });
};

export const formatDateTime = (dateStr: string): string => {
  const date = toUTC(dateStr);
  return date.toLocaleDateString('en-IN', {
    ...IST,
    day: 'numeric',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
};

export const formatTime = (dateStr: string): string => {
  const date = toUTC(dateStr);
  return date.toLocaleTimeString('en-IN', {
    ...IST,
    hour: '2-digit',
    minute: '2-digit',
  });
};

/**
 * Format an appointment slot time given as "HH:MM" (24h) into "9:45 AM".
 * Unlike formatTime, it treats the value as a wall-clock slot, NOT a UTC
 * datetime — a bare "HH:MM" must never be shifted by the IST offset.
 */
export const formatSlotTime = (hhmm?: string | null): string => {
  if (!hhmm) return '';
  const [h, m] = hhmm.split(':').map((n) => Number(n));
  if (Number.isNaN(h)) return hhmm;
  const period = h >= 12 ? 'PM' : 'AM';
  const h12 = h % 12 || 12;
  return `${h12}:${String(Number.isNaN(m) ? 0 : m).padStart(2, '0')} ${period}`;
};

export const formatBookingId = (id: string): string => {
  return id.length > 8 ? `...${id.slice(-8)}` : id;
};

export const getTimeAgo = (dateStr: string): string => {
  const now = new Date();
  const date = toUTC(dateStr);
  const diffMs = now.getTime() - date.getTime();
  const diffMins = Math.floor(diffMs / 60000);
  const diffHrs = Math.floor(diffMs / 3600000);
  const diffDays = Math.floor(diffMs / 86400000);

  if (diffMins < 1) return 'Just now';
  if (diffMins < 60) return `${diffMins}m ago`;
  if (diffHrs < 24) return `${diffHrs}h ago`;
  if (diffDays < 7) return `${diffDays}d ago`;
  return formatDate(dateStr);
};

/** "8:00 am" from "08:00". Minutes are always shown so a column of times lines
 *  up instead of alternating between "8 am" and "8:30 am". */
export function spokenTime(time: string): string {
  const [h24, mins] = (time || '').split(':').map(Number);
  if (Number.isNaN(h24)) return time;
  const hour = h24 % 12 === 0 ? 12 : h24 % 12;
  const suffix = h24 < 12 ? 'am' : 'pm';
  return `${hour}:${String(mins || 0).padStart(2, '0')} ${suffix}`;
}
